import { ReactNode, useMemo, useState } from "react"
import { Business, Contact, Household } from "@/schema"
import { InlineSelect } from "@ui/forms/inline"
import { Building2, Contact as ContactIcon, Home, type LucideIcon } from "lucide-react"

import { useFetchBusinesses } from "@/hooks/api/businesses"
import { useFetchContacts } from "@/hooks/api/contacts"
import { useFetchHouseholds } from "@/hooks/api/households"
import PeekView from "@/components/peek-view"

const CONTACT_VALUE_PREFIX = "contact:"
const HOUSEHOLD_VALUE_PREFIX = "household:"
const BUSINESS_VALUE_PREFIX = "business:"

export interface InlineRelationshipSelectProps {
  label?: ReactNode
  LabelIcon?: LucideIcon
  selectedHousehold?: Household | null
  selectedContact?: Contact | null
  selectedBusiness?: Business | null
  setSelectedHouseholdId?: (id: string | null) => void
  setSelectedContactId?: (id: string | null) => void
  setSelectedBusinessId?: (id: string | null) => void
  onSave?: (householdId: string | null, contactId: string | null, businessId: string | null) => void | Promise<void>
  isDisabled?: boolean
  fixedLabelWidth?: string
  hideLabel?: boolean
  size?: "sm" | "base" | "lg"
  allowHouseholds?: boolean
  allowContacts?: boolean
  allowBusinesses?: boolean
  householdType?: "prospect" | "client" | null
  showPeekView?: boolean
  bookId?: string
}

function getOptionValue(type: "contact" | "household" | "business", id: string) {
  if (type === "contact") return `${CONTACT_VALUE_PREFIX}${id}`
  if (type === "household") return `${HOUSEHOLD_VALUE_PREFIX}${id}`
  return `${BUSINESS_VALUE_PREFIX}${id}`
}

export function InlineRelationshipSelect({
  label,
  LabelIcon,
  selectedHousehold,
  selectedContact,
  selectedBusiness,
  setSelectedHouseholdId,
  setSelectedContactId,
  setSelectedBusinessId,
  onSave,
  isDisabled,
  fixedLabelWidth,
  hideLabel,
  size,
  allowHouseholds = true,
  allowContacts = true,
  allowBusinesses = true,
  householdType,
  showPeekView = false,
  bookId,
}: InlineRelationshipSelectProps) {
  const [searchQuery, setSearchQuery] = useState("")
  const contactsSearchTerm = allowContacts && searchQuery.trim().length > 0 ? searchQuery : undefined
  const { data: contactsData, isPending: isFetchingContacts } = useFetchContacts(contactsSearchTerm, bookId)
  const { data: householdsData, isLoading: isFetchingHouseholds } = useFetchHouseholds(
    allowHouseholds ? searchQuery : undefined,
    householdType ?? undefined,
    bookId
  )
  const businessesSearchTerm = allowBusinesses && searchQuery.trim().length > 0 ? searchQuery : undefined
  const { data: businessesData, isLoading: isFetchingBusinesses } = useFetchBusinesses(businessesSearchTerm, bookId)

  const options = useMemo(() => {
    const nextOptions: Array<{ value: string; label: string; icon: ReactNode }> = []

    // Add selected options first
    if (selectedHousehold && selectedHousehold.id) {
      nextOptions.push({
        value: getOptionValue("household", selectedHousehold.id),
        label: selectedHousehold.name || "Unnamed household",
        icon: <Home size={16} strokeWidth={1.5} />,
      })
    }

    if (selectedContact && selectedContact.id) {
      nextOptions.push({
        value: getOptionValue("contact", selectedContact.id),
        label: selectedContact.person?.full_name || "Unnamed contact",
        icon: <ContactIcon size={16} strokeWidth={1.5} />,
      })
    }

    if (selectedBusiness && selectedBusiness.id) {
      nextOptions.push({
        value: getOptionValue("business", selectedBusiness.id),
        label: selectedBusiness.name || "Unnamed business",
        icon: <Building2 size={16} strokeWidth={1.5} />,
      })
    }

    // Add household options
    if (allowHouseholds && householdsData?.households) {
      const householdOptions = householdsData.households
        .filter((household) => household.id !== selectedHousehold?.id)
        .map((household) => ({
          value: getOptionValue("household", household.id),
          label: household.name || "Unnamed household",
          icon: <Home size={16} strokeWidth={1.5} />,
        }))
      nextOptions.push(...householdOptions)
    }

    // Add contact options
    if (allowContacts && contactsData?.contacts) {
      const contactOptions = contactsData.contacts
        .filter((contact) => contact.id !== selectedContact?.id)
        .map((contact) => ({
          value: getOptionValue("contact", contact.id),
          label: contact.person?.full_name || "Unnamed contact",
          icon: <ContactIcon size={16} strokeWidth={1.5} />,
        }))
      nextOptions.push(...contactOptions)
    }

    if (allowBusinesses && businessesData?.businesses) {
      const businessOptions = businessesData.businesses
        .filter((business) => business.id !== selectedBusiness?.id)
        .map((business) => ({
          value: getOptionValue("business", business.id),
          label: business.name || "Unnamed business",
          icon: <Building2 size={16} strokeWidth={1.5} />,
        }))
      nextOptions.push(...businessOptions)
    }

    return nextOptions
  }, [
    householdsData?.households,
    contactsData?.contacts,
    businessesData?.businesses,
    selectedHousehold,
    selectedContact,
    selectedBusiness,
    allowHouseholds,
    allowContacts,
    allowBusinesses,
  ])

  const handleValueChange = async (value: string | null) => {
    if (!value) {
      setSelectedHouseholdId?.(null)
      setSelectedContactId?.(null)
      setSelectedBusinessId?.(null)
      if (onSave) {
        await onSave(null, null, null)
      }
      return
    }

    if (value.startsWith(CONTACT_VALUE_PREFIX)) {
      const contactId = value.replace(CONTACT_VALUE_PREFIX, "")
      setSelectedContactId?.(contactId)
      setSelectedHouseholdId?.(null)
      setSelectedBusinessId?.(null)
      if (onSave) {
        await onSave(null, contactId, null)
      }
    } else if (value.startsWith(HOUSEHOLD_VALUE_PREFIX)) {
      const householdId = value.replace(HOUSEHOLD_VALUE_PREFIX, "")
      setSelectedHouseholdId?.(householdId)
      setSelectedContactId?.(null)
      setSelectedBusinessId?.(null)
      if (onSave) {
        await onSave(householdId, null, null)
      }
    } else if (value.startsWith(BUSINESS_VALUE_PREFIX)) {
      const businessId = value.replace(BUSINESS_VALUE_PREFIX, "")
      setSelectedBusinessId?.(businessId)
      setSelectedHouseholdId?.(null)
      setSelectedContactId?.(null)
      if (onSave) {
        await onSave(null, null, businessId)
      }
    }
  }

  const handleSearch = (query: string) => {
    setSearchQuery(query)
  }

  const currentValue = selectedContact
    ? getOptionValue("contact", selectedContact.id)
    : selectedHousehold
      ? getOptionValue("household", selectedHousehold.id)
      : selectedBusiness
        ? getOptionValue("business", selectedBusiness.id)
        : null

  const displayLabel = selectedContact
    ? selectedContact.person?.full_name || "Unnamed contact"
    : selectedHousehold
      ? selectedHousehold.name || "Unnamed household"
      : selectedBusiness
        ? selectedBusiness.name || "Unnamed business"
        : "-"

  const isLoading =
    (allowContacts && isFetchingContacts) ||
    (allowHouseholds && isFetchingHouseholds) ||
    (allowBusinesses && isFetchingBusinesses)

  return (
    <InlineSelect
      label={label}
      LabelIcon={LabelIcon}
      defaultValue={currentValue}
      placeholder={isLoading ? "Loading..." : "Select relationship..."}
      options={options}
      disabled={isLoading || isDisabled}
      onSave={handleValueChange}
      onSearch={handleSearch}
      isSearching={isLoading}
      fixedLabelWidth={fixedLabelWidth}
      hideLoader
      hideLabel={hideLabel}
      size={size}
      renderValue={(option) => {
        if (!option) {
          return displayLabel
        }

        // Show PeekView if enabled and a relationship is selected
        if (showPeekView) {
          // In this component, option.label is always a string (see line 70 type definition)
          const labelString = option.label as string
          if (selectedContact?.id) {
            return (
              <PeekView
                label={labelString}
                recordType="Contact"
                recordId={selectedContact.id}
                size="sm"
                disableTriggerLink
              />
            )
          }
          if (selectedHousehold?.id && selectedHousehold?.type) {
            return (
              <PeekView
                label={labelString}
                recordType={selectedHousehold.type}
                recordId={selectedHousehold.id}
                size="sm"
                disableTriggerLink
              />
            )
          }
          if (selectedBusiness?.id) {
            return (
              <PeekView
                label={labelString}
                recordType="Business"
                recordId={selectedBusiness.id}
                size="sm"
                disableTriggerLink
              />
            )
          }
        }

        // Default display with icon
        return (
          <div className="flex items-center gap-2">
            {option.icon}
            <span>{option.label}</span>
          </div>
        )
      }}
    />
  )
}
